
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  const { t } = useTranslation(['common', 'about']);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-32 bg-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/40"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link
                  to="/"
                  className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                >
                  {t('common:nav.home')}
                </Link>
              </li>
              <li className="text-gray-500">/</li>
              <li className="text-white font-medium">{t('common:nav.about')}</li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <h1
              className="text-6xl font-bold text-white mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {t('about:hero.title')}
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              {t('about:hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2
              className="text-4xl font-bold text-gray-900 mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {t('about:company.title')}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              {t('about:company.paragraph1')}
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              {t('about:company.paragraph2')}
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              {t('about:company.paragraph3')}
            </p>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {t('about:targetAudience.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('about:targetAudience.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {['dealers', 'interiorStores', 'projectFurnishers'].map((audience) => (
              <div
                key={audience}
                className="bg-white rounded-3xl p-10 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-6">
                  <i
                    className={`${
                      audience === 'dealers'
                        ? 'ri-store-2-line'
                        : audience === 'interiorStores'
                        ? 'ri-home-4-line'
                        : 'ri-building-line'
                    } text-3xl text-amber-600`}
                    style={{ width: '36px', height: '36px' }}
                  ></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {t(`about:targetAudience.${audience}.title`)}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t(`about:targetAudience.${audience}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {t('about:coreValues.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {['quality', 'design', 'reliability', 'scalability'].map((value) => (
              <div key={value} className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                  <i
                    className={`${
                      value === 'quality'
                        ? 'ri-medal-line'
                        : value === 'design'
                        ? 'ri-palette-line'
                        : value === 'reliability'
                        ? 'ri-shield-check-line'
                        : 'ri-global-line'
                    } text-4xl text-amber-600`}
                    style={{ width: '36px', height: '36px' }}
                  ></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t(`about:coreValues.${value}.title`)}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t(`about:coreValues.${value}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* European Coverage */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {t('about:europeanCoverage.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('about:europeanCoverage.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              'deutschland',
              'niederlande',
              'belgien',
              'frankreich',
              'polen',
              'osterreich',
              'schweiz',
              'italien',
              'spanien',
              'uk',
            ].map((country) => (
              <div
                key={country}
                className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="text-3xl mb-2">
                  {country === 'deutschland'
                    ? '🇩🇪'
                    : country === 'niederlande'
                    ? '🇳🇱'
                    : country === 'belgien'
                    ? '🇧🇪'
                    : country === 'frankreich'
                    ? '🇫🇷'
                    : country === 'polen'
                    ? '🇵🇱'
                    : country === 'osterreich'
                    ? '🇦🇹'
                    : country === 'schweiz'
                    ? '🇨🇭'
                    : country === 'italien'
                    ? '🇮🇹'
                    : country === 'spanien'
                    ? '🇪🇸'
                    : '🇬🇧'}
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {t(`about:europeanCoverage.countries.${country}`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Only */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-amber-500/20 rounded-full mb-6">
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-400">
              {t('about:b2bOnly.badge')}
            </span>
          </div>
          <h2
            className="text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {t('about:b2bOnly.title')}
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed mb-10">
            {t('about:b2bOnly.description')}
          </p>
          <Link
            to="/dealer-registration"
            className="inline-block px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg cursor-pointer whitespace-nowrap"
          >
            {t('common:nav.becomeDealer')}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
